import React, {useState, useEffect} from 'react';
import {View, Text, TextInput} from 'react-native';
import {DynamicTextBoxProps} from '../../Types/ControlTypes';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {MongoIdSqlId} from '../../Types/DtoTypes';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';

const DynamicTextBox = (props: DynamicTextBoxProps): React.ReactElement => {
  const [value, setValue] = useState(props.value ?? '');
  const [isFocused, setIsFocused] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [position, setPosition] = useState(0);
  const [autofill, setAutofill] = useState(props.autofill);

  const defaultMaxLength = 100;

  const {equipmentVehicleProfiles} = usePowerSyncData();

  useEffect(() => {
    if (props.value && props.value != value) {
      setValue(props.value);
    }
  }, [props.value]);

  useEffect(() => {
    if (isFocused === false) {
      let valid = validate();

      if (props.value === value) return;

      let metadataKey: string | null = null;
      if (props.config?.hasMetaData && props.config?.metaDataKey)
        metadataKey = props.config.metaDataKey;

      if (props.onChange)
        props.onChange(
          props.controlId,
          props.controlTypeId,
          value,
          valid,
          metadataKey,
          value,
        );
    }
  }, [isFocused]);

  useEffect(() => {
    const getAutofillValue = async () => {
      if (autofill && autofill.value) {
        if (autofill.condition === 'LicensePlateNumber') {
          try {
            let valObj = JSON.parse(autofill.value) as MongoIdSqlId;

            let vehicle = equipmentVehicleProfiles.filter(
              x => x._id == valObj.mongoId,
            )[0];

            if (vehicle != null) {
              let newValue = vehicle.licensePlate;

              let metadataKey: string | null = null;
              setValue(newValue);

              if (props.config?.hasMetaData && props.config?.metaDataKey) {
                metadataKey = props.config.metaDataKey;
              }

              if (props.onChange)
                props.onChange(
                  props.controlId,
                  props.controlTypeId,
                  newValue,
                  true,
                  metadataKey,
                  newValue,
                );
            }
          } catch (error) {
            console.log('Error: Bad TextBox value serialization');
          }
        }
      }
    };

    getAutofillValue();
  }, [autofill]);

  useEffect(() => {
    if (
      props.autofill &&
      JSON.stringify(props.autofill) !== JSON.stringify(autofill)
    )
      setAutofill(props.autofill);
  }, [props.autofill]);

  useEffect(() => {
    if (props.showError) validate();
  }, [props.showError]);

  function focus(): void {
    setIsFocused(true);

    if (props.onFocus) props.onFocus(position);
  }

  function validate(): boolean {
    let isValid = true;
    let error = '';

    if (props.config?.required && value === '') {
      isValid = false;
      error =
        (props.label ? '"' + props.label + '"' : 'This field') + ' is required';
    } else if (props.config?.validationRegExp && value !== '') {
      var regExp = new RegExp(props.config?.validationRegExp, 'g');
      if (!regExp.test(value)) {
        isValid = false;
        if (props.config?.validationRegMsg)
          error = props.config?.validationRegMsg;
        else
          error =
            'Invalid ' + (props.label ? '"' + props.label + '"' : ' field');
      }
    }

    if (props.showError || isFocused !== null) setError(error);

    return isValid;
  }

  return (
    <View
      style={{display: props.visible === false ? 'none' : 'flex'}}
      onLayout={event => setPosition(event.nativeEvent.layout.y)}>
      {props.label ? (
        <Text style={ControlsStyleSheet.label}>
          {props.label}
          <Text style={ControlsStyleSheet.required}>
            {props.config?.required ? '*' : ''}
          </Text>
        </Text>
      ) : null}
      <TextInput
        style={[
          ControlsStyleSheet.input,
          isFocused && ControlsStyleSheet.inputFocused,
          error !== '' && ControlsStyleSheet.inputError,
          isFocused && error !== '' && ControlsStyleSheet.inputErrorFocused,
          {
            color: props.config?.color ?? ControlsStyleSheet.input.color,
            fontSize:
              props.config?.fontSize ?? ControlsStyleSheet.input.fontSize,
            fontWeight:
              props.config?.fontWeight ?? ControlsStyleSheet.input.fontWeight,
          },
          props.config?.backGroundColor !== undefined && {
            backgroundColor: props.config?.backGroundColor,
          },
          props.config?.borderWidth !== undefined && {
            borderWidth: props.config?.borderWidth,
          },
          props.disabled && {
            opacity: 0.5,
          },
        ]}
        placeholder={props.config?.placeholder}
        placeholderTextColor={ControlsStyleSheet.placeholder.color}
        maxLength={props.config?.maxLength ?? defaultMaxLength}
        value={value}
        onChangeText={newText => setValue(newText)}
        onFocus={focus}
        onBlur={() => setIsFocused(false)}
        editable={!props.disabled}
      />
      <Text style={ControlsStyleSheet.error}>{error}</Text>
    </View>
  );
};

export default DynamicTextBox;
